import {FormInstance} from 'antd/es/form';

import {EEditableInput} from 'components/editable-table/model/types';
import {EFormMode} from 'components/form';
import {ICreateConditionCallbacksArgs, TValueTypes} from 'components/form/hooks/use-form-watcher/use-form-watcher';
import {EComparisonType} from 'components/form/hooks/use-form-watcher/use-form-watcher-constants';
import {getCustomWatchers} from 'components/form/hooks/use-form-watcher/use-form-watcher-custom-watchers';
import {EFieldType, IFieldMeta, IValidationRules} from 'entities/metadata';
import {TEntityValue} from 'modules/data';
import {
    EFormFieldWatcherActions,
    EFormFieldWatcherConditions,
    EFormFieldWatcherConnective,
} from 'shared/constants/watchers';
import {IFormFieldWatcher, IFormFieldWatcherCondition, TDisabledDateConfig} from 'shared/types/watchers';
import {evaluateBoolean} from 'shared/utils/boolean-evaluator';
import {createConnectiveJoiner} from 'shared/utils/watchers';

interface IWatchersResult {
    isHidden: boolean;
    isRequired: boolean;
    isDisabled: boolean;
    isAllowedEmpty: boolean;
    validationRules?: IValidationRules[];
    referenceUrlByWatcher?: string;
    disabledDateConfig?: TDisabledDateConfig;
}

const compareValues = (
    value: TValueTypes,
    comparableValue: TValueTypes,
    comparisonType: string,
) => {
    switch (comparisonType) {
    case EComparisonType.EQUALS:
        return value === comparableValue;
    case EComparisonType.GREATER:
        return value > comparableValue;
    case EComparisonType.GREATER_OR_EQUAL:
        return value >= comparableValue;
    case EComparisonType.LESS:
        return value < comparableValue;
    case EComparisonType.LESS_OR_EQUAL:
        return value <= comparableValue;
    default:
        return false;
    }
};

const checkWatcherCondition = (
    args: ICreateConditionCallbacksArgs & {
        formMode?: EFormMode;
        fieldMeta: IFieldMeta;
    }): boolean => {
    const {
        condition,
        ownValue,
        withOwnValueAsNumber,
        targetValue,
        withValue,
        withKey,
        withValueAsNumber,
        formMode,
        fieldMeta
    } = args;

    const ownValueNormalized = withOwnValueAsNumber ? Number(ownValue) : ownValue || !!ownValue;
    const targetValueNormalized = withValueAsNumber ? Number(targetValue) : targetValue || !!targetValue;
    const withValueNormalized = withValueAsNumber ? Number(withValue) : withValue;

    const callbacks = {
        [EFormFieldWatcherConditions.always]: true,
        [EFormFieldWatcherConditions.equalsByKeyOrToValue]: (() => {
            if (withKey && targetValue?.[withKey]) {
                const normalized = withValueAsNumber ? Number(targetValue[withKey]) : targetValue[withKey];
                return normalized === withValueNormalized;
            }
            return targetValueNormalized === withValueNormalized;
        })(),
        [EFormFieldWatcherConditions.formCreating]: formMode === EFormMode.CREATE,
        [EFormFieldWatcherConditions.formEditing]: formMode === EFormMode.EDIT,
        [EFormFieldWatcherConditions.set]: !!targetValueNormalized,
        [EFormFieldWatcherConditions.isFieldSet]: (() => {
            if (!withKey) return false;
            return !!targetValueNormalized[withKey];
        })(),
        [EFormFieldWatcherConditions.unset]: !targetValueNormalized || targetValueNormalized.length === 0,
        [EFormFieldWatcherConditions.equals]: ownValueNormalized === targetValueNormalized,
        [EFormFieldWatcherConditions.fieldEqualsToValue]: (() => {
            if (!withKey) return false;
            const normalized = withValueAsNumber ? Number(targetValue?.[withKey]) : targetValue?.[withKey];
            return normalized === withValueNormalized;
        })(),
        [EFormFieldWatcherConditions.fieldEqualsToValues]: (() => {
            if (!withKey) return false;
            return String(withValue)
                ?.split(',')
                .map(value => value.trim())
                .includes(targetValue?.[withKey]);
        })(),
        [EFormFieldWatcherConditions.compareFieldValues]: (() => {
            if (!withValue) return false;
            if (fieldMeta.type === EFieldType.NUMBER || fieldMeta.inputType === EEditableInput.INPUT_NUMBER) {
                return compareValues(Number(ownValue), Number(targetValue), withValue);
            }
            return compareValues(ownValue, targetValue, withValue);
        })(),
        [EFormFieldWatcherConditions.equalsToValue]: targetValueNormalized === withValueNormalized,
        [EFormFieldWatcherConditions.notEqualsToValue]: withValueNormalized !== targetValueNormalized,
        // TODO: в editable-table не используется, реализовать при рефакторе для использования в других формах
        [EFormFieldWatcherConditions.refererenceValueHasBeenChanged]: (() => false)(),
        // Так же не используется в editable-table
        [EFormFieldWatcherConditions.hasRole]: false,
        // Для использования в useTableCellWatcher
        [EFormFieldWatcherConditions.unique]: false,
    };

    return callbacks[condition] ?? false;
};

const applyWatcherAction = (
    action: EFormFieldWatcherActions,
    context: {
        isWatcherTriggered: boolean | null;
        watcher: IFormFieldWatcher;
        fieldMeta: IFieldMeta;
        formValues: {[key: string]: TEntityValue};
        form: FormInstance;
        result: IWatchersResult;
    }
) => {
    const { isWatcherTriggered, watcher, fieldMeta, form, result } = context;

    if (!isWatcherTriggered) return;

    switch (action) {
    case EFormFieldWatcherActions.disable:
        result.isDisabled = true;
        break;
    case EFormFieldWatcherActions.enableField:
        result.isDisabled = false;
        break;
    case EFormFieldWatcherActions.require:
        result.isRequired = true;
        break;
    case EFormFieldWatcherActions.hide:
        result.isHidden = true;
        break;
    case EFormFieldWatcherActions.setOwnValue:
        if (watcher.withOwnValueToSet !== undefined) {
            form.setFieldsValue({ [fieldMeta.key]: watcher.withOwnValueToSet });
        }
        break;
    case EFormFieldWatcherActions.allowEmpty:
        result.isAllowedEmpty = true;
        break;
    case EFormFieldWatcherActions.addValidationRules:
        if (watcher.withValidationRules) {
            result.validationRules?.push(...watcher.withValidationRules);
        }
        break;
    case EFormFieldWatcherActions.addFieldError:
        if (watcher.withErrorToSet) {
            result.validationRules?.push({
                mask: 'errorWatcher',
                errorMessage: watcher.withErrorToSet
            });
        }
        break;
    case EFormFieldWatcherActions.clear:
        // TODO: в editable-table не используется, реализовать при рефакторе для использования в других формах
        break;
    default:
    }
};

export const executeFormWatchers = (
    formValues: {[key: string]: TEntityValue},
    fieldMeta?: IFieldMeta,
    form?: FormInstance,
    formMode?: EFormMode,
    entityName?: string,
) => {
    const result: IWatchersResult = {
        isHidden: false,
        isRequired: false,
        isDisabled: false,
        isAllowedEmpty: false,
        validationRules: [],
        referenceUrlByWatcher: undefined,
        disabledDateConfig: undefined
    };
    if (!fieldMeta) return result;

    const fieldWatchers = getCustomWatchers(fieldMeta, entityName);

    if (!fieldWatchers?.length) return result;

    fieldWatchers.forEach((watcher: IFormFieldWatcher) => {
        const {
            conditions,
            perform: actions,
            withTargetToPickAttributeFrom,
            withTargetAttributeKey,
            withMapping,
            conditionsExpression,
        } = watcher;
        const ownValue = formValues[fieldMeta.key];
        let isWatcherTriggered: boolean | null = null;

        if (conditionsExpression) {
            let expression = conditionsExpression;

            conditions.forEach(({
                target,
                when: condition,
                withValue,
                withKey,
                name,
                withValueAsNumber,
                dateRange,
            }: IFormFieldWatcherCondition) => {
                if (name) {
                    expression = expression.split(name).join((() => {
                        const targetValue = target ? formValues[target] : undefined;
                        const isTriggered = checkWatcherCondition({
                            condition,
                            targetValue,
                            ownValue,
                            withKey,
                            withValue,
                            withValueAsNumber,
                            withOwnValueAsNumber: watcher.withOwnValueAsNumber,
                            target,
                            withTargetToPickAttributeFrom,
                            withTargetAttributeKey,
                            withMapping,
                            dateRange,
                            formMode,
                            fieldMeta
                        });
                        return `${isTriggered}`;
                    })());
                }
            });

            isWatcherTriggered = evaluateBoolean(expression);
        } else {
            conditions?.forEach(({
                target,
                when: condition,
                withValue,
                withKey,
                withConnective,
                withValueAsNumber,
                dateRange,
            }: IFormFieldWatcherCondition) => {
                const targetValue = target ? formValues[target] : undefined;
                const isTriggered = checkWatcherCondition({
                    condition,
                    targetValue,
                    ownValue,
                    withKey,
                    withValue,
                    withValueAsNumber,
                    withOwnValueAsNumber: watcher.withOwnValueAsNumber,
                    target,
                    withTargetToPickAttributeFrom,
                    withTargetAttributeKey,
                    withMapping,
                    dateRange,
                    formMode,
                    fieldMeta
                });

                const joinCondition = createConnectiveJoiner(withConnective);

                if (isWatcherTriggered === null) {
                    isWatcherTriggered = withConnective === EFormFieldWatcherConnective.NOT
                        ? !isTriggered : isTriggered;
                } else {
                    isWatcherTriggered = joinCondition(isWatcherTriggered, isTriggered);
                }
            });
        }

        // Применяем действия
        const actionArray = Array.isArray(actions) ? actions : [actions];

        if (form) {
            actionArray.forEach(action => {
                applyWatcherAction(action, {
                    isWatcherTriggered,
                    watcher,
                    fieldMeta,
                    formValues,
                    form,
                    result
                });
            });
        }
    });

    return result;
};